import { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import CloseIcon from "assets/icons/close.svg?react";
import DownloadIcon from "assets/icons/download.svg?react";
import PDFViewer from "components/PDFViewer";

import KeysContext from "contexts/KeysContext";
import usePDF from "hooks/usePDF";
import dispatchChartService from "api/dispatchChartService";
import { IAttachment } from "dto/IAttachment";
import { IKey } from "dto/IKey";
import { ItemKind } from "dto/IKindItems";
import { EParcelConfirmationFileKind } from "dto/Mailroom/IDispatchChart";

import styles from "./styles.module.scss";

/**
 * Źródło podglądu:
 * - `confirmation` — skan zwrotki ZPO / potwierdzenia odbioru / karty nadawczej. Plik jest
 *   szyfrowany kluczem FIRMY, więc pobieramy go z kluczami obiektu i deszyfrujemy klientowo
 *   (jak podgląd treści pisma w biurze podawczym).
 * - `blob` — gotowy plik (np. jawny dowód UPP/UPD/EPO pobrany z serwera).
 */
export type ConfirmationPreviewSource =
  | {
      kind: "confirmation";
      parcelId: string;
      fileKind: EParcelConfirmationFileKind;
    }
  | { kind: "blob"; blob: Blob; fileName?: string; contentType?: string };

interface Props {
  title: string;
  source: ConfirmationPreviewSource;
  onClose: () => void;
}

const isImage = (contentType?: string | null, fileName?: string | null) =>
  (contentType ?? "").toLowerCase().startsWith("image/") ||
  /\.(png|jpe?g|gif|bmp|webp|tiff?)$/i.test(fileName ?? "");

const isPdf = (contentType?: string | null, fileName?: string | null) =>
  (contentType ?? "").toLowerCase().includes("pdf") ||
  /\.pdf$/i.test(fileName ?? "");

/**
 * Podgląd potwierdzenia doręczenia (Magistrat: „Podgląd ZPO" / „Podgląd potwierdzenia
 * nadania" / podgląd dowodu). Portal na body — otwierany też z popupów w komórkach DataGrid.
 */
const ConfirmationPreviewModal = ({ title, source, onClose }: Props) => {
  const { t } = useTranslation();
  const confirmation = source.kind === "confirmation" ? source : null;

  const { data } = dispatchChartService.useGetConfirmationFile(
    confirmation?.parcelId ?? "",
    confirmation?.fileKind ?? EParcelConfirmationFileKind.Reception,
    !!confirmation
  );
  const preview = data?.data;

  const { keys, setKeys } = useContext(KeysContext);
  const { decryptPackage } = usePDF();

  const [blob, setBlob] = useState<Blob | null>(
    source.kind === "blob" ? source.blob : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gotowy plik (dowód) — bez pobierania i deszyfrowania.
  useEffect(() => {
    if (source.kind === "blob") {
      setBlob(source.blob);
      setError(null);
    }
  }, [source]);

  // Klucze obiektu + nadrzędne do KeysContext (bez nich deszyfrowanie = nokey).
  useEffect(() => {
    if (!preview) return;
    const incoming: IKey[] = [
      ...(preview.ObjectKeys ?? []),
      ...(preview.Keys ?? []),
    ];
    const missing = incoming.filter((k) => !keys.some((x) => x.Id === k.Id));
    if (missing.length > 0) setKeys([...keys, ...missing]);
  }, [preview, keys, setKeys]);

  const attachment: Partial<IAttachment> | null = useMemo(() => {
    if (!preview?.Uri) return null;
    return {
      Id: preview.Uri,
      Uri: preview.Uri,
      Kind: preview.Kind as ItemKind,
      EncryptionKeyId: preview.EncryptionKeyId ?? undefined,
      FileName: preview.FileName ?? "potwierdzenie.pdf",
      ContentType: preview.ContentType ?? "application/pdf",
      Format: isImage(preview.ContentType, preview.FileName) ? "image" : "pdf",
      Label: preview.FileName ?? "",
      Size: 0,
    };
  }, [preview]);

  // Plik jawny (EncryptionKeyId=null) nie potrzebuje klucza; szyfrowany — czeka na klucz.
  const keyReady =
    !!attachment &&
    (!attachment.EncryptionKeyId ||
      keys.some((k) => k.Id === attachment.EncryptionKeyId));

  useEffect(() => {
    if (source.kind !== "confirmation") return;
    let cancelled = false;
    setBlob(null);
    setError(null);
    if (!attachment || !keyReady) return;
    setLoading(true);
    (async () => {
      try {
        // isDownloading=true → surowy odszyfrowany blob (bez konwersji przez wtyczkę —
        // skan bywa obrazem, nie dokumentem edytora).
        const b = await decryptPackage(attachment, attachment.Kind, true);
        if (!cancelled) setBlob(b);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment?.Uri, attachment?.EncryptionKeyId, keyReady, source.kind]);

  const fileName =
    source.kind === "blob"
      ? source.fileName ?? "dowod.pdf"
      : preview?.FileName ?? "potwierdzenie.pdf";
  const contentType =
    source.kind === "blob"
      ? source.contentType ?? source.blob.type
      : preview?.ContentType ?? "application/pdf";

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob || !isImage(contentType, fileName)) {
      setImageUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob, contentType, fileName]);

  const download = () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const body = () => {
    if (error) {
      return (
        <div className={styles.error}>
          {t("metric.delivery.decryptError")} ({error})
        </div>
      );
    }
    if (!blob) {
      return (
        <div className={styles.empty}>
          {loading
            ? t("metric.delivery.decrypting")
            : t("metric.delivery.loading")}
        </div>
      );
    }
    if (imageUrl) {
      return (
        <div className={styles.imageWrap}>
          <img src={imageUrl} alt={fileName} className={styles.image} />
        </div>
      );
    }
    if (isPdf(contentType, fileName)) {
      return <PDFViewer file={blob} title={fileName} />;
    }
    return (
      <div className={styles.empty}>
        {t("metric.delivery.noPreview")}
        <div className={styles.downloadLink} onClick={download}>
          {t("metric.delivery.download")}
        </div>
      </div>
    );
  };

  // UWAGA: klików (onClick) NIE zatrzymujemy — zdarzenia syntetyczne muszą dobąbelkować
  // przez portal do ClickAwayListenera poppera metryki, inaczej MUI uzna klik w podgląd
  // za „poza" i zamknie metrykę razem z podglądem. Rodzice, którym klik z portalu
  // przeszkadza (badge na Dysku), sami go ignorują, gdy podgląd jest otwarty.
  // Tło zamyka na click (nie mousedown): po zamknięciu na mousedown podgląd znikał przed
  // clickiem, a ten trafiał w nakładkę poppera metryki i zamykał także metrykę.
  return createPortal(
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal}>
        <div className={styles.head}>
          <span className={styles.title}>{title}</span>
          <div className={styles.actions}>
            {blob && (
              <div
                className={styles.iconBtn}
                title={t("metric.delivery.download")}
                onClick={download}
              >
                <DownloadIcon />
              </div>
            )}
            <CloseIcon className={styles.closeIcon} onClick={onClose} />
          </div>
        </div>
        <div className={styles.body}>{body()}</div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationPreviewModal;
